import { createContext, useContext, useEffect, useState } from "react";

type SidebarPlacement = "top" | "left";
type ColorPalette = "brand" | "neutral";

interface SettingsContextType {
  sidebarPlacement: SidebarPlacement;
  setSidebarPlacement: (placement: SidebarPlacement) => void;
  colorPalette: ColorPalette;
  setColorPalette: (palette: ColorPalette) => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [sidebarPlacement, setSidebarPlacement] = useState<SidebarPlacement>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("sidebarPlacement") as SidebarPlacement;
      return stored || "left";
    }
    return "left";
  });

  const [colorPalette, setColorPalette] = useState<ColorPalette>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("colorPalette") as ColorPalette;
      return stored || "brand";
    }
    return "brand";
  });

  useEffect(() => {
    localStorage.setItem("sidebarPlacement", sidebarPlacement);
  }, [sidebarPlacement]);

  useEffect(() => {
    localStorage.setItem("colorPalette", colorPalette);
    const root = document.documentElement;
    root.classList.remove("palette-brand", "palette-neutral");
    root.classList.add(`palette-${colorPalette}`);
  }, [colorPalette]);

  return (
    <SettingsContext.Provider value={{ 
      sidebarPlacement, 
      setSidebarPlacement,
      colorPalette,
      setColorPalette
    }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
}
